import { HqlCode } from '@/components/hql/HqlCode';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { appliesToDraftResultsState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import type { ConsiderationType } from '@rock/types';
import type { FC } from 'react';

export const AppliesToDraftResultsTable: FC<{ considerationType: ConsiderationType }> = ({ considerationType }) => {
    const onRowClick = useRowClick();
    const [results, resultsLoadable] = useLoadableAtomValue(appliesToDraftResultsState(considerationType.id), []);

    if (resultsLoadable.state === 'loading') {
        return (
            <Card>
                <CardContent className="p-6 text-muted-foreground">Loading...</CardContent>
            </Card>
        );
    }
    if (resultsLoadable.state === 'hasError') {
        return (
            <Card>
                <CardContent className="p-6 text-destructive">
                    Error loading query drafts:{' '}
                    {resultsLoadable.error instanceof Error
                        ? resultsLoadable.error.message
                        : `${resultsLoadable.error}`}
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="border-b-1 pb-3">
                <CardTitle className="text-xl">Query Drafts</CardTitle>
            </CardHeader>
            <CardContent>
                {results.length === 0 ? (
                    <div className="text-muted-foreground">No query drafts for this consideration type yet.</div>
                ) : (
                    <div className="px-4 pb-4">
                        <Table className="not-prose">
                            <TableHeader className="bg-secondary">
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Query</TableHead>
                                    <TableHead>Valid</TableHead>
                                    <TableHead>Summary</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {results.map((result) => (
                                    <TableRow
                                        key={result.id}
                                        onClick={onRowClick(
                                            `/content/considerations/${considerationType.id}/results/query-drafts/${result.id}`,
                                        )}
                                        className="cursor-pointer"
                                    >
                                        <TableCell>{new Date(result.createdAt).toLocaleString()}</TableCell>
                                        <TableCell className="max-w-md truncate">
                                            {result.result.query ? (
                                                <HqlCode query={result.result.query} />
                                            ) : (
                                                <span className="font-mono text-xs text-muted-foreground">none</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {!result.result.query ? (
                                                <span className="text-amber-600">No query</span>
                                            ) : result.validationErrors.length > 0 ? (
                                                <span className="text-destructive">Invalid</span>
                                            ) : (
                                                <span className="text-green-600">Yes</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="max-w-md truncate">{result.result.summary}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
